import React, { useState } from 'react';
import { X } from 'lucide-react';
import { db, MatterTemplate } from '../../lib/db';
import { toast } from 'sonner';

interface CreateFromMatterTemplateModalProps {
  isOpen: boolean;
  onClose: () => void;
  template: MatterTemplate | null;
  userId: string;
  onSuccess: () => void;
}

export function CreateFromMatterTemplateModal({
  isOpen,
  onClose,
  template,
  userId,
  onSuccess,
}: CreateFromMatterTemplateModalProps) {
  const [year, setYear] = useState(new Date().getFullYear());
  const [creating, setCreating] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!template) return;

    setCreating(true);
    try {
      await db.createMatterFromTemplate(template.id, userId, year);
      toast.success('Matter created successfully from template');
      onSuccess();
      onClose();
    } catch (error) {
      console.error('Error creating matter from template:', error);
      toast.error('Failed to create matter from template');
    } finally {
      setCreating(false);
    }
  };

  if (!isOpen || !template) return null;

  const years = Array.from(
    { length: 5 },
    (_, i) => new Date().getFullYear() + i
  );

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      <div className="absolute inset-0 bg-gray-500 bg-opacity-75" onClick={onClose} />
      
      <div className="absolute inset-x-4 top-1/2 -translate-y-1/2 max-w-md mx-auto bg-white rounded-lg shadow-xl">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900">Create Matter from Template</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500 transition-colors"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          <div className="mb-6">
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {template.title}
            </h3>
            {template.description && (
              <p className="text-sm text-gray-600">{template.description}</p>
            )}
          </div>

          <div className="mb-6">
            <label htmlFor="year" className="block text-sm font-medium text-gray-700 mb-1">
              Select Year for Deliverables
            </label>
            <select
              id="year"
              value={year}
              onChange={(e) => setYear(Number(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            >
              {years.map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>
            <p className="mt-1 text-sm text-gray-500">
              All deliverables in this matter will be created for the selected year
            </p>
          </div>

          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
              disabled={creating}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 text-sm font-medium text-white bg-purple-600 rounded-md hover:bg-purple-700 transition-colors disabled:opacity-50"
              disabled={creating}
            >
              {creating ? 'Creating...' : 'Create Matter'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}